import React from "react";
import {
  Editor,
  EditorState,
  CompositeDecorator,
  ContentBlock,
  ContentState,
  Modifier,
  SelectionState
} from "draft-js";
import Mark from "../Mark/Mark";
import { Container } from "./Styled";

const decorator = (editor: any) =>
  new CompositeDecorator([
    {
      strategy: findLinkEntities,
      component: Mark,
      props: {
        onRemoveEntity: editor.remove,
        onChangeEntity: editor.updateEntity,
        lock: editor.lock,
        unlock: editor.unlock
      }
    }
  ]);

function findLinkEntities(
  contentBlock: ContentBlock,
  callback: (start: number, end: number) => void,
  contentState: ContentState
) {
  contentBlock.findEntityRanges(character => {
    const entityKey = character.getEntity();
    return (
      entityKey !== null &&
      contentState.getEntity(entityKey).getType() === "LINK"
    );
  }, callback);
}

type EntityEditorProps = {
  value: EditorState;
  onChange: (editorState: EditorState) => void;
  onBlur: () => void;
};

type EntityEditorState = {
  readOnly: boolean;
};

const moveSelectionToEnd = (editorState: EditorState) => {
  const content = editorState.getCurrentContent();
  const blockMap = content.getBlockMap();

  const key = blockMap.last().getKey();
  const length = blockMap.last().getLength();
  const selection = new SelectionState({
    anchorKey: key,
    anchorOffset: length,
    focusKey: key,
    focusOffset: length
  });

  return EditorState.acceptSelection(editorState, selection);
};

class EntityEditor extends React.Component<
  EntityEditorProps,
  EntityEditorState
> {
  constructor(props: EntityEditorProps) {
    super(props);
    if (props.value) {
      // @ts-ignore
      props.onChange(
        EditorState.createWithContent(
          props.value.getCurrentContent(),
          decorator(this)
        )
      );
    } else {
      props.onChange(EditorState.createEmpty(decorator(this)));
    }
    this.state = {
      readOnly: false
    };
  }

  lock = () => {
    this.setState({ readOnly: true });
  };

  unlock = () => {
    this.setState({ readOnly: false });
  };

  onChange = (editorState: EditorState) => {
    const selection = editorState.getSelection();
    if (!selection.isCollapsed()) {
      const contentState = editorState.getCurrentContent();
      const contentStateWithEntity = contentState.createEntity(
        "LINK",
        "IMMUTABLE"
      );
      const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

      var contentStateUpdated = Modifier.applyEntity(
        contentStateWithEntity,
        selection,
        entityKey
      );
      var newEditorState = EditorState.set(editorState, {
        currentContent: contentStateUpdated
      });
      this.props.onChange(moveSelectionToEnd(newEditorState));
      return;
    } else {
      this.props.onChange(editorState);
    }
  };

  remove = (blockKey: string, start: number, end: number) => {
    if (!this.state.readOnly) {
      const contentState = this.props.value.getCurrentContent();
      var block = this.props.value.getCurrentContent().getBlockForKey(blockKey);
      const selection = this.props.value.getSelection();
      const selectionOfAtomicBlock = selection.merge({
        anchorKey: block.getKey(),
        anchorOffset: start,
        focusKey: block.getKey(),
        focusOffset: end
      });
      const newContentState = Modifier.applyEntity(
        contentState,
        // @ts-ignore
        selectionOfAtomicBlock,
        null
      );
      const newEditorState = EditorState.push(
        this.props.value,
        newContentState,
        "apply-entity"
      );

      this.props.onChange(newEditorState);
    }
  };

  updateEntity = (entityId: number, entityKey: string) => {
    const content = this.props.value.getCurrentContent();
    const contentWithNewData = content.mergeEntityData(entityKey, {
      entityId: entityId
    });

    const newEditorState = EditorState.push(
      this.props.value,
      contentWithNewData,
      "change-block-data"
    );

    this.props.onChange(newEditorState);
    this.props.onBlur();
  };

  render() {
    // @ts-ignore
    return (
      <Container>
        <Editor
          readOnly={this.state.readOnly}
          ref="editor"
          editorState={this.props.value}
          onChange={editorState => this.onChange(editorState)}
          onBlur={() => {
            this.props.onBlur();
          }}
        />
      </Container>
    );
  }
}

export default EntityEditor;
